import type {InstancedMesh, Mesh, Object3D} from "three";
import {observable, makeObservable} from "mobx";
import type {SpaceViewRenderer} from "../../renderers/SpaceViewRenderer";
import type {GrabbableCorner} from "../../elements3d/GrabbableCorner";
import type {SpaceItem} from "../../elements3d/SpaceItem";
import type {Xyicon3D} from "../../elements3d/Xyicon3D";
import type {BoundarySpaceMap3D} from "../../elements3d/BoundarySpaceMap3D";
import type {Markup3D} from "../../elements3d/markups/abstract/Markup3D";
import {BoundingBox} from "../../elements3d/BoundingBox";
import {LinkLineManager} from "../LinkLineManager";
import {SnapToGridManager} from "../SnapToGridManager";
import {MarkupTextManager} from "../MSDF/MarkupTextManager";
import {CaptionCollisionSolver} from "../../features/CaptionCollisionSolver";
import type {Xyicon} from "../../../../../../../data/models/Xyicon";
import type {Boundary} from "../../../../../../../data/models/Boundary";
import type {Markup} from "../../../../../../../data/models/Markup";
import type {ISpaceItemModel} from "../../../../../../../data/models/Model";
import type {BoundarySpaceMap} from "../../../../../../../data/models/BoundarySpaceMap";
import type {IFilterState} from "../../../../../../../data/models/filter/Filter";
import {createFilterState, filterModels} from "../../../../../../../data/models/filter/Filter";
import type {Dimensions, PointDouble, PortTemplateDto} from "../../../../../../../generated/api/base";
import {XyiconFeature, Permission} from "../../../../../../../generated/api/base";
import {Signal} from "../../../../../../../utils/signal/Signal";
import {KeyboardListener} from "../../../../../../../utils/interaction/key/KeyboardListener";
import {THREEUtils} from "../../../../../../../utils/THREEUtils";
import {PopupUtils} from "../../../../../abstract/popups/PopupUtils";
import {DebugInformation} from "../../../../../../../utils/DebugInformation";
import {MathUtils} from "../../../../../../../utils/math/MathUtils";
import {PopupUtilsV5} from "../../../../../../5.0/popup/PopupUtilsV5";
import type {CaptionedItem} from "../MSDF/CaptionManager";
import {TimeUtils} from "../../../../../../../utils/TimeUtils";
import {notifyAboutInsufficientUpdatePermissionForSelectedItems, notifyAboutViewFilteringOutItems} from "../../renderers/SpaceViewRendererUtils";
import {SelectionTool} from "../../features/tools/SelectionTool";
import {LinkIconManager} from "./icons/LinkIconManager";
import {RaycasterManager} from "./RaycasterManager";
import {RotationIconManager} from "./icons/RotationIconManager";
import {MarkupManager} from "./MarkupManager";
import {BoundaryManager} from "./BoundaryManager";
import {XyiconManager} from "./XyiconManager";
import {LockIconManager} from "./icons/LockIconManager";
import {SelectionHandleManager} from "./SelectionHandleManager";
import {
	canSingleSelectedItemEnterEditMode,
	getMarkupSelectionPadding,
	getSelectionBoxBoundingBox,
	isV5LinearMarkupType,
} from "./SelectionHandleUtils";

/**
 * This class contains the managers for items that can appear on the spaceeditor
 */

export class SpaceItemController {
	private readonly _spaceViewRenderer: SpaceViewRenderer;
	private readonly _xyiconManager: XyiconManager;
	private readonly _boundaryManager: BoundaryManager;
	private readonly _markupManager: MarkupManager;
	private readonly _boundingBox: BoundingBox;
	private _isTranslating: boolean = false;
	private readonly _rotationIconManager: RotationIconManager;
	private readonly _selectionHandleManager: SelectionHandleManager;
	private readonly _linkIconManager: LinkIconManager;
	private readonly _linkLineManager: LinkLineManager;
	private readonly _lockIconManager: LockIconManager;
	private readonly _raycasterManager: RaycasterManager;
	private readonly _snapToGridManager: SnapToGridManager;
	private readonly _savedCursorPos: PointDouble = {x: null, y: null};
	private readonly _savedBBoxPos: PointDouble = {x: null, y: null};
	private readonly _delta: Dimensions = {x: 0, y: 0, z: 0};
	private _markupTextManager: MarkupTextManager;
	private readonly _captionCollisionSolver: CaptionCollisionSolver;
	private _previousSelectedItemIds: string = "";
	private _updateFilterStateTimeoutId: number = -1;
	private _updateFilterStatePromise: Promise<void> = null;
	private readonly _updateFilterStatePromiseResolveSet: Set<(value: void | PromiseLike<void>) => void> = new Set();
	private _refreshHoverAfterRenderListener: (() => void) | null = null;

	@observable
	public filterState: IFilterState = createFilterState();

	private readonly _directions: {[key: string]: PointDouble} = {
		[KeyboardListener.KEY_ARROW_UP]: {x: 0, y: 1},
		[KeyboardListener.KEY_ARROW_RIGHT]: {x: 1, y: 0},
		[KeyboardListener.KEY_ARROW_DOWN]: {x: 0, y: -1},
		[KeyboardListener.KEY_ARROW_LEFT]: {x: -1, y: 0},
	};
	private _isPopupOpen: boolean = false;
	public signals = {
		filterUpdated: Signal.create(),
		itemsTranslated: Signal.create(),
		itemsRotated: Signal.create(),
		editedItemModified: Signal.create(),
		actionBarUpdated: Signal.create(),
		canvasItemClicked: Signal.create<ISpaceItemModel>(),
	};

	constructor(spaceViewRenderer: SpaceViewRenderer) {
		makeObservable(this);
		this._spaceViewRenderer = spaceViewRenderer;

		this._rotationIconManager = new RotationIconManager(this._spaceViewRenderer);
		this._selectionHandleManager = new SelectionHandleManager(this._spaceViewRenderer);
		this._linkIconManager = new LinkIconManager(this._spaceViewRenderer);
		this._linkLineManager = new LinkLineManager(this._spaceViewRenderer);
		this._lockIconManager = new LockIconManager(this._spaceViewRenderer);
		this._raycasterManager = new RaycasterManager(this._spaceViewRenderer);
		this._snapToGridManager = new SnapToGridManager(this._spaceViewRenderer);

		this._markupManager = new MarkupManager(this._spaceViewRenderer);
		this._xyiconManager = new XyiconManager(this._spaceViewRenderer);
		this._boundaryManager = new BoundaryManager(this._spaceViewRenderer);

		this._captionCollisionSolver = new CaptionCollisionSolver(this._spaceViewRenderer);

		this._boundingBox = new BoundingBox(this._spaceViewRenderer);
	}

	private get _inheritedMethods() {
		return this._spaceViewRenderer.inheritedMethods;
	}

	public async init(xyicons: Xyicon[], boundaries: Boundary[], markups: Markup[]) {
		this._snapToGridManager.init();
		this._spaceViewRenderer.toolManager.cameraControls.scrollbars.init();
		this._rotationIconManager.init();
		this._selectionHandleManager.init();
		this._linkIconManager.init();
		this._lockIconManager.init();
		this._boundingBox.initZIndex();
		this._markupManager.init();
		this._xyiconManager.init();
		this._boundaryManager.init();

		await this.populateData(xyicons, boundaries, markups);

		this._markupManager.signals.itemsRemove.add(this.onMarkupsRemoved);

		this._linkIconManager.update();
		this._lockIconManager.update();

		KeyboardListener.getInstance().signals.down.add(this.onKeyDown);
		KeyboardListener.getInstance().signals.up.add(this.onKeyUp);
		this._spaceViewRenderer.toolManager.cameraControls.signals.cameraZoomChange.add(this.onCameraZoomChange);
		this.onCameraZoomChange();
	}

	private readonly onMarkupsRemoved = (markups: Markup[]) => {
		this._markupTextManager.recreateGeometry();
	};

	private readonly onKeyDown = (event: KeyboardEvent) => {
		switch (event.key) {
			case "c":
				if (!event.ctrlKey) {
					this._boundaryManager.hideCaptions();
					this._xyiconManager.hideCaptions();
				}
				break;
		}
	};

	private readonly onKeyUp = (event: KeyboardEvent) => {
		switch (event.key) {
			case "c":
				this._xyiconManager.showCaptions();
				this._boundaryManager.showCaptions();
				break;
		}

		const directionNames = [
			KeyboardListener.KEY_ARROW_UP,
			KeyboardListener.KEY_ARROW_RIGHT,
			KeyboardListener.KEY_ARROW_DOWN,
			KeyboardListener.KEY_ARROW_LEFT,
		];
		const selectedItems = this.selectedItems;

		if (directionNames.includes(event.key) && selectedItems.length > 0) {
			const correctionMultiplier = this._spaceViewRenderer.correctionMultiplier.current;
			const cameraZoomFactor = this._spaceViewRenderer.currentZoomValue;
			const multiplier = (correctionMultiplier * (KeyboardListener.isShiftDown ? 10 : 1)) / cameraZoomFactor;

			const finalDirection = {...this._directions[event.key]};

			finalDirection.x *= multiplier;
			finalDirection.y *= multiplier;

			const wasSnapToGridActive = this._snapToGridManager.isActive;

			this._snapToGridManager.deactivate();

			const bboxPos = this._boundingBox.position;

			this.startTranslatingSelectedItems(bboxPos.x, bboxPos.y);
			this.translateSelectedItems(finalDirection.x, finalDirection.y, 0);
			this.stopTranslatingSelectedItems();

			if (wasSnapToGridActive) {
				this._snapToGridManager.activate();
			}
		}
	};

	private readonly onCameraZoomChange = () => {
		const hasSelectedItems = this.selectedItems.length > 0;

		if (hasSelectedItems) {
			this._rotationIconManager.updateTransformations();
			this._selectionHandleManager.updateTransformations();
		}

		this._raycasterManager.updateLineThreshold();

		const activeTool = this._spaceViewRenderer.toolManager.activeTool;

		if (activeTool instanceof SelectionTool && hasSelectedItems) {
			this.scheduleHoverRefreshAfterRender();
		} else {
			this.clearScheduledHoverRefreshAfterRender();
		}
	};

	private clearScheduledHoverRefreshAfterRender() {
		if (!this._refreshHoverAfterRenderListener) {
			return;
		}

		this._spaceViewRenderer.signals.onAfterRender.remove(this._refreshHoverAfterRenderListener);
		this._refreshHoverAfterRenderListener = null;
	}

	public scheduleHoverRefreshAfterRender() {
		this.clearScheduledHoverRefreshAfterRender();

		this._refreshHoverAfterRenderListener = () => {
			this._refreshHoverAfterRenderListener = null;

			const activeTool = this._spaceViewRenderer.toolManager.activeTool;

			if (activeTool instanceof SelectionTool) {
				activeTool.refreshHoverAtSavedPointer();
			}
		};

		this._spaceViewRenderer.signals.onAfterRender.addOnce(this._refreshHoverAfterRenderListener);
	}

	public getMeshAtCoords(domX: number, domY: number, intersectables?: Object3D[], excludedSpaceItemIds: string[] = []) {
		return this._raycasterManager.getMeshAtCoords(domX, domY, intersectables, excludedSpaceItemIds);
	}

	public clearData() {
		this.clearScheduledHoverRefreshAfterRender();
		this.filterState = createFilterState();
		this.deselectAll();
		this._xyiconManager.clear();
		this._boundaryManager.clear();
		this._markupManager.clear();
		this._rotationIconManager.clear();
		this._selectionHandleManager.clear();

		KeyboardListener.getInstance().signals.down.remove(this.onKeyDown);
		KeyboardListener.getInstance().signals.up.remove(this.onKeyUp);
		this._markupManager.signals.itemsRemove.remove(this.onMarkupsRemoved);
		this._spaceViewRenderer.toolManager.cameraControls.signals.cameraZoomChange.remove(this.onCameraZoomChange);
		this._markupTextManager?.dispose();
	}

	private async populateData(xyicons: Xyicon[], boundaries: Boundary[], markups: Markup[]) {
		const xyiconLogId = "Putting xyicons on the space";
		const boundaryLogId = "Putting boundaries on the space";
		const markupLogId = "Putting markups on the space";

		DebugInformation.start(xyiconLogId);
		await this._xyiconManager.initXyicons(xyicons);
		DebugInformation.end(xyiconLogId);

		DebugInformation.start(boundaryLogId);
		await this._boundaryManager.initBoundaries(boundaries);
		DebugInformation.end(boundaryLogId);

		DebugInformation.start(markupLogId);
		this._markupTextManager?.clear();
		this._markupTextManager = new MarkupTextManager(this._spaceViewRenderer);
		await this._markupManager.initMarkups(markups);
		await this.loadMarkupTexts();
		DebugInformation.end(markupLogId);
	}

	private loadMarkupTexts() {
		return this._markupTextManager.init();
	}

	private clearSelectionVisuals() {
		this._rotationIconManager.removeIcons();
		this._boundingBox.reset();
		this._selectionHandleManager.clear();
		this._spaceViewRenderer.needsRender = true;
	}

	public deselectAll(updateUI: boolean = true, closeEmbeddedWindow: boolean = true, unfocusAll: boolean = true, deselectLinks: boolean = true) {
		this._xyiconManager.deselectAll();
		this._boundaryManager.deselectAll();
		this._markupManager.deselectAll();
		if (deselectLinks) {
			this._linkLineManager.deselectAll();
		}
		if (unfocusAll) {
			this._inheritedMethods.focusItems([]);
		}
		this.clearSelectionVisuals();

		if (updateUI) {
			this.closeActionBar();
			this.closePortSelector();
			this.closeContextMenu();
			this.closeLinks();
			if (closeEmbeddedWindow) {
				this.closeEmbeddedWindow();
			}
			this.updateDetailsPanel(true);
		}
	}

	public getSpaceItemFromMeshAtCoords(meshAtCoords: Mesh | InstancedMesh): Xyicon3D | BoundarySpaceMap3D | Markup3D {
		return this._raycasterManager.getSpaceItemFromMeshAtCoords(meshAtCoords) as Xyicon3D | BoundarySpaceMap3D | Markup3D;
	}

	/**
	 * Saves current pos of selected items
	 * xy: current worldpos of cursor
	 */
	public startTranslatingSelectedItems(startX: number, startY: number) {
		this._isTranslating = true;
		this._rotationIconManager.removeIcons();
		this._xyiconManager.startTranslatingSelectedItems();
		this._boundaryManager.startTranslatingSelectedItems();
		this._markupManager.startTranslatingSelectedItems();
		this.updateBoundingBox();
		this._savedCursorPos.x = startX;
		this._savedCursorPos.y = startY;

		const bbox = this._boundingBox.boundingBox;

		// we agreed that we should snap the top left corner of the selectionbox, not the center
		this._savedBBoxPos.x = bbox.min.x;
		this._savedBBoxPos.y = bbox.max.y;

		this.closeEmbeddedWindow();

		if (this._markupManager.selectedItems.length > 0) {
			this._markupTextManager.updateTextTransformations();
		}
	}

	/**
	 * // world coordinates, measured from pointerStart (NOT from the last pointerposition) -> this way it's more reliable
	 * @param x
	 * @param y
	 */
	public translateSelectedItems(x: number, y: number, z: number) {
		this._delta.x = x;
		this._delta.y = y;
		this._delta.z = z;

		if (this._snapToGridManager.isActive) {
			const cursorPosToBBoxPos = {
				x: this._savedBBoxPos.x - this._savedCursorPos.x,
				y: this._savedBBoxPos.y - this._savedCursorPos.y,
			};

			const finalPos = this._snapToGridManager.getUpdatedCoords(
				this._savedCursorPos.x + x + cursorPosToBBoxPos.x,
				this._savedCursorPos.y + y + cursorPosToBBoxPos.y,
			);

			this._delta.x = finalPos.x - this._savedBBoxPos.x;
			this._delta.y = finalPos.y - this._savedBBoxPos.y;
		}

		// Z translate is only supported for xyicons at the moment
		this._xyiconManager.translateSelectedItems(this._delta.x, this._delta.y, this._delta.z);
		this._boundaryManager.translateSelectedItems(this._delta.x, this._delta.y, 0);
		this._markupManager.translateSelectedItems(this._delta.x, this._delta.y, 0);
		this._spaceViewRenderer.needsRender = true;
		this.closeActionBar();
		this.closePortSelector();
		this.closeContextMenu();
		this.updateBoundingBox();
		this._selectionHandleManager.updateTransformations();

		if (this._markupManager.selectedItems.length > 0) {
			this._markupTextManager.updateTextTransformations();
		}

		this.signals.itemsTranslated.dispatch();
	}

	public stopTranslatingSelectedItems() {
		if (this._isTranslating) {
			this._xyiconManager.stopTranslatingSelectedItems();
			this._boundaryManager.stopTranslatingSelectedItems();
			this._markupManager.stopTranslatingSelectedItems();
			this._isTranslating = false;

			if (this._delta.x !== 0 || this._delta.y !== 0) {
				this.fixCaptionCollisionsForSelected();
			}

			this._selectionHandleManager.update();
		}
	}

	public updateActionBar(selectDetailsContent: boolean = true, updateDetailsPanel: boolean = true) {
		const selectedItems = this.selectedItems;

		if (!this._spaceViewRenderer.toolManager.isInLinkMode && selectedItems.length > 0) {
			if (!this.syncLegacyRotationIcons()) {
				const isAllEmbedded = selectedItems.every(
					(spaceItem: SpaceItem) => spaceItem.spaceItemType === "xyicon" && (spaceItem.modelData as Xyicon).isEmbedded,
				);

				if (isAllEmbedded) {
					this.closeActionBar();
				} else {
					this.updateBoundingBox();
					// adding 15 + 15 px additional height
					const additionalHeight = 30 * this._spaceViewRenderer.correctionMultiplier.current;
					const {worldX, worldY} = this.getActionBarWorldPosition(selectedItems, additionalHeight);

					const hasPermissionToOpenActionBar = this._spaceViewRenderer.isV5
						? this._spaceViewRenderer.actions.someSpaceItemsHaveGivenPermission(selectedItems, Permission.View) // in V5 the 'i' icon still should be visible
						: this._spaceViewRenderer.actions.someSpaceItemsHaveGivenPermission(selectedItems, Permission.Update);

					if (hasPermissionToOpenActionBar) {
						const hasChanged = this._inheritedMethods.openActionBar(worldX, worldY);
						if (hasChanged) {
							this.signals.actionBarUpdated.dispatch();
						}
					}
				}
			}

			this._selectionHandleManager.update();
		} else {
			this.closeActionBar();
			if (!this._spaceViewRenderer.toolManager.linkManager.isInLinkMode) {
				this.closePortSelector();
			}
			this._boundingBox.reset();
			this._selectionHandleManager.clear();
		}

		if (updateDetailsPanel) {
			this.updateDetailsPanel(selectDetailsContent);
		}
	}

	private getActionBarWorldPosition(selectedItems: SpaceItem[], additionalHeight: number) {
		const selectedItem = selectedItems[0];

		if (
			this._spaceViewRenderer.isV5 &&
			selectedItems.length === 1 &&
			selectedItem?.spaceItemType === "markup" &&
			!isV5LinearMarkupType((selectedItem as Markup3D).type)
		) {
			const selectedMarkup = selectedItem as Markup3D;
			const padding = getMarkupSelectionPadding(
				selectedMarkup.type,
				this._spaceViewRenderer.correctionMultiplier.current,
				this._spaceViewRenderer.toolManager.cameraControls.cameraZoomValue,
				selectedMarkup.lineThickness,
			);
			const boundingBox = getSelectionBoxBoundingBox(selectedItem.position, selectedItem.scale, selectedItem.orientation, padding);
			const center = THREEUtils.getCenterOfBoundingBox(boundingBox);

			return {
				worldX: center.x,
				worldY: boundingBox.max.y + additionalHeight,
			};
		}

		return {
			worldX: this._boundingBox.position.x,
			worldY: this._boundingBox.boundingBox.max.y + additionalHeight,
		};
	}

	public openPortSelector(worldX: number, worldY: number, item: Xyicon, ports: PortTemplateDto[], type: "from" | "to") {
		this._inheritedMethods.openPortSelector(worldX, worldY, item, ports, type);
	}

	public closeActionBar() {
		this._inheritedMethods?.onExitTextEditMode?.();
		const hasChanged = this._inheritedMethods?.closeActionBar();
		if (hasChanged) {
			this.signals.actionBarUpdated.dispatch();
		}
	}

	public closeContextMenu() {
		this._inheritedMethods?.closeContextMenu();
	}

	public closePortSelector() {
		this._inheritedMethods?.closePortSelector();
	}

	public openLinks(fromObjectIds: string[], changeSelection: boolean) {
		if (changeSelection) {
			this.deselectAll();
			const xyiconsToSelect = fromObjectIds.map((objectAID: string) => this._xyiconManager.getItemById(objectAID) as Xyicon3D);

			for (const xyiconToSelect of xyiconsToSelect) {
				if (xyiconToSelect?.isVisible) {
					xyiconToSelect.select();
				}
			}

			this.updateDetailsPanel(true);
		}
		this._inheritedMethods?.openLinkBreakers(fromObjectIds);
		this._inheritedMethods?.openLinkedXyiconsWindow(fromObjectIds, "external");
	}

	public updateLinkBreakers() {
		this._inheritedMethods?.updateLinkBreakers();
	}

	private clearFocusedLinkDetails() {
		const focusedItems = this._inheritedMethods.getFocusedItems();

		if (focusedItems.some((item) => item.ownFeature === XyiconFeature.Link)) {
			this._inheritedMethods.focusItems([]);
		}
	}

	public closeLinks() {
		this._linkLineManager.deselectAll();
		this.clearFocusedLinkDetails();
		this._inheritedMethods?.closeLinkBreakers();
	}

	public updateEmbeddedWindow(objectAID: string) {
		this._inheritedMethods?.openLinkedXyiconsWindow([objectAID], "embedded");
	}

	private closeEmbeddedWindow() {
		this._inheritedMethods?.closeLinkedXyiconsWindow();
	}

	public updateDetailsPanel(selectDetailsContent?: boolean, shouldFocusItem?: boolean) {
		if (this._inheritedMethods?.selectItems) {
			const selectedItems = this.selectedItems;
			const selectedItemIds = selectedItems.map((item) => item.id).join();
			const selectedItemModels: ISpaceItemModel[] = selectedItems.map((item: SpaceItem) => item.modelData) as ISpaceItemModel[];
			const filteredSelectedItemModels = selectedItemModels.filter((model) => !!model);

			if (selectedItemModels.length !== filteredSelectedItemModels.length) {
				console.warn("Some selected items are missing modelData. Please report this to the developers.");
			}
			const forceUpdate = this._previousSelectedItemIds !== selectedItemIds;

			if (forceUpdate) {
				this._inheritedMethods.onExitTextEditMode?.();
			}

			this._inheritedMethods.selectItems(filteredSelectedItemModels, selectDetailsContent, forceUpdate);
			if (shouldFocusItem) {
				this._inheritedMethods.focusItems(filteredSelectedItemModels);
			}
			this._previousSelectedItemIds = selectedItemIds;
		}
	}

	public updateBoundingBox() {
		if (this._spaceViewRenderer.isV5 && this.selectedItems.length > 1) {
			const v5GroupBoundingBox = this._selectionHandleManager.getSelectedCanvasBoundingBox();

			if (v5GroupBoundingBox) {
				this._boundingBox.setFromBoundingBoxes([v5GroupBoundingBox]);
			} else {
				this._boundingBox.reset();
			}
		} else {
			const xyiconBBox = this._xyiconManager.selectionBox.boundingBox;
			const boundaryBBox = this._boundaryManager.selectionBox.boundingBox;
			const markupBBox = this._markupManager.selectionBox.boundingBox;

			this._boundingBox.setFromBoundingBoxes([xyiconBBox, boundaryBBox, markupBBox]);
		}

		if (this.isInEditMode || (this._spaceViewRenderer.isV5 && this.selectedItems.length <= 1)) {
			this._boundingBox.hide();
		} else {
			this._boundingBox.show();
		}
	}

	private getNumberOfSelectedItems() {
		const {selectedItems} = this;

		let numberOfEmbeddedXyicons = 0;

		for (const selectedItem of selectedItems) {
			if (selectedItem.spaceItemType === "xyicon") {
				const xyicon = selectedItem.modelData as Xyicon;

				numberOfEmbeddedXyicons += xyicon.embeddedXyicons.length;
			}
		}

		return selectedItems.length + numberOfEmbeddedXyicons;
	}

	public onDeleteClick = async () => {
		if (!this._isPopupOpen) {
			const numberOfSelectedItems = this.getNumberOfSelectedItems();

			if (numberOfSelectedItems > 0) {
				this._isPopupOpen = true;

				const getDeleteConfirmationPopup =
					this._spaceViewRenderer.transport.appState.currentUIVersion == "5.0"
						? PopupUtilsV5.getDeleteConfirmationPopupV5
						: PopupUtils.getDeleteConfirmationPopup;

				const confirmed = await getDeleteConfirmationPopup(
					[XyiconFeature.Xyicon, XyiconFeature.Boundary, XyiconFeature.Markup],
					numberOfSelectedItems,
				);

				this._isPopupOpen = false;
				if (confirmed) {
					this.deleteSelectedItems();
					this.closeActionBar();
					this.closePortSelector();
				}
			}
		}
	};

	private async deleteSelectedItems() {
		const linksNeedUpdate = this._xyiconManager.selectedItems.some((xyicon: Xyicon3D) => xyicon.linkInstanceId > -1);
		const lockNeedUpdate = [...this._xyiconManager.selectedItems, ...this._boundaryManager.selectedItems, ...this._markupManager.selectedItems].some(
			(item: SpaceItem) => item.isPositionLockedAnyWay,
		);

		const promises = [
			this._xyiconManager.deleteSelectedItems(),
			this._boundaryManager.deleteSelectedItems(),
			this._markupManager.deleteSelectedItems(),
		];

		this.clearSelectionVisuals();
		this.updateDetailsPanel(true);

		await Promise.all(promises);
		if (linksNeedUpdate) {
			this._linkIconManager.update();
		}
		if (lockNeedUpdate) {
			this._lockIconManager.update();
		}
	}

	public selectItemsInsideRectangle(smallerX: number, smallerY: number, largerX: number, largerY: number) {
		this._xyiconManager.selectItemsInsideRectangle(smallerX, smallerY, largerX, largerY);
		this._boundaryManager.selectItemsInsideRectangle(smallerX, smallerY, largerX, largerY);
		this._markupManager.selectItemsInsideRectangle(smallerX, smallerY, largerX, largerY);

		this.updateDetailsPanel(true);

		// to render on top
		this.updateMarkupTextsForSelected();
	}

	private updateMarkupTextsForSelected() {
		if (this._markupManager.selectedItems.length > 0) {
			this._markupTextManager.updateTextTransformations();
		}
	}

	private syncLegacyRotationIcons() {
		const currentlyEditedMarkup = this.currentlyEditedItem as Markup3D;

		if (this._spaceViewRenderer.isV5 && !currentlyEditedMarkup?.isTextOffsetHandlerVisible) {
			this._rotationIconManager.clear();
			return false;
		}

		if (this._rotationIconManager.isRotationModeOn) {
			// Preserve the legacy V4 markup-text rotation icon instead of rebuilding it from the generic selected-item set.
			if (currentlyEditedMarkup?.isTextOffsetHandlerVisible) {
				return true;
			}

			this._rotationIconManager.update();
			return true;
		}

		return false;
	}

	public startRotatingSelectedItems(pivot?: PointDouble) {
		this._xyiconManager.startRotatingSelectedItems(pivot);
		this._boundaryManager.startRotatingSelectedItems(pivot);
		this._markupManager.startRotatingSelectedItems(pivot);
		this._xyiconManager.hideCaptionsForSelected();
		this._boundaryManager.hideCaptionsForSelected();
	}

	/**
	 * Rotates selected items
	 * @param deltaAngle delta angle in radian, related to the starting angle, NOT the previous angle
	 */
	public rotateSelectedItems(delta: number, pivot?: PointDouble) {
		const newDelta = pivot ? MathUtils.calculateNewOrientation(0, delta) : delta;

		this._xyiconManager.rotateSelectedItems(newDelta, pivot);
		this._boundaryManager.rotateSelectedItems(newDelta, pivot);
		this._markupManager.rotateSelectedItems(newDelta, pivot);

		if (pivot) {
			this._boundingBox.rotate(newDelta);
		} else {
			this._boundingBox.hide();
		}

		this._rotationIconManager.updateTransformations();
		this._selectionHandleManager.updateTransformations();
		this.updateMarkupTextsForSelected();
		this.signals.itemsRotated.dispatch();
	}

	public stopRotatingSelectedItems() {
		if (this.selectedItems.length > 0) {
			this._xyiconManager.showCaptionsForSelected();
			this._boundaryManager.showCaptionsForSelected();
			this._xyiconManager.stopRotatingSelectedItems();
			this._boundaryManager.stopRotatingSelectedItems();
			this._markupManager.stopRotatingSelectedItems();
			this.fixCaptionCollisionsForSelected();
			this._boundingBox.stopRotation();
			this.updateMarkupTextsForSelected();
			this.updateBoundingBox();
			this.updateActionBar();
			this.syncLegacyRotationIcons();
			this._selectionHandleManager.update();
		}
	}

	public fixCaptionCollisionsForSelected() {
		if (this._xyiconManager.selectedItems.length + this._boundaryManager.selectedItems.length > 0) {
			const selectionBox = this._boundingBox;
			const scale = selectionBox.scale;
			const maxRadius = Math.max(scale.x, scale.y) + this._xyiconManager.xyiconSize * 10;
			const center = selectionBox.position;

			this._captionCollisionSolver.updateCaptionsInARadius(center.x, center.y, maxRadius);
		}
	}

	private getEditedItemsManager() {
		return this._boundaryManager.selectedItems.length === 1 ? this._boundaryManager : (this._markupManager as BoundaryManager | MarkupManager);
	}

	private canEnterEditModeForSelectedItem() {
		if (this.selectedItems.length !== 1) {
			return false;
		}

		const selectedItem = this.selectedItems[0];
		const permission =
			selectedItem?.modelData != null
				? this._spaceViewRenderer.actions.getModuleTypePermission(selectedItem.modelData.typeId, selectedItem.modelData.ownFeature)
				: null;

		return canSingleSelectedItemEnterEditMode(selectedItem?.spaceItemType, permission);
	}

	public switchEditMode(value: boolean) {
		if (value && !this.canEnterEditModeForSelectedItem()) {
			notifyAboutInsufficientUpdatePermissionForSelectedItems(this._spaceViewRenderer);
			return;
		}

		this.getEditedItemsManager().switchEditMode(value);
		if (value) {
			this._boundingBox.hide();
		} else {
			this.updateBoundingBox();
			this.fixCaptionCollisionsForSelected();
		}
		this._selectionHandleManager.update();
		this._inheritedMethods?.onEditModeSwitched(value);
	}

	public onRedrawClick() {
		const {currentlyEditedItem} = this._boundaryManager;

		if (currentlyEditedItem) {
			this._boundaryManager.currentlyRedrawnBoundary = currentlyEditedItem as BoundarySpaceMap3D;
			const modelData = currentlyEditedItem.modelData as BoundarySpaceMap;

			currentlyEditedItem.destroy(false);
			this._boundaryManager.setTypeId(modelData.typeId);
			this._inheritedMethods?.setActiveTool("boundary");
			this._boundaryManager.switchEditMode(false);
			this.closeActionBar();
		}
	}

	public onCancelEditClick() {
		this.getEditedItemsManager().cancelEditMode();
		this.updateMarkupTextsForSelected();
		this.updateBoundingBox();
		this._selectionHandleManager.update();
		this.signals.editedItemModified.dispatch();
	}

	public onGrabbableCornerPointerDown(grabbableCorner: GrabbableCorner) {
		this._boundaryManager.onGrabbableCornerPointerDown(grabbableCorner);
		this._markupManager.onGrabbableCornerPointerDown(grabbableCorner);
	}

	public onGrabbableCornerPointerMove(deltaX: number, deltaY: number) {
		this._boundaryManager.onGrabbableCornerPointerMove(deltaX, deltaY);
		this._markupManager.onGrabbableCornerPointerMove(deltaX, deltaY);
		if (this._markupManager.isInEditMode) {
			this._markupTextManager.updateTextTransformations();
		}
		this.signals.editedItemModified.dispatch();
	}

	public onGrabbableCornerPointerUp() {
		this._boundaryManager.onGrabbableCornerPointerUp();
		this._markupManager.onGrabbableCornerPointerUp();
	}

	private isModelInSpace = (spaceItem: ISpaceItemModel) => spaceItem.spaceId === this._spaceViewRenderer.space?.id;

	public async onLayerSettingsModified(updateRotationIconManager: boolean = true, updateFilterState: boolean = true) {
		if (updateRotationIconManager) {
			const wasThereSelectedItems = this.selectedItems.length > 0;

			if (wasThereSelectedItems) {
				const {spaceItemController} = this._spaceViewRenderer;

				spaceItemController.updateActionBar(false);
				spaceItemController.syncLegacyRotationIcons();
				spaceItemController.selectionHandleManager.update();
			}
		}

		if (updateFilterState) {
			this._spaceViewRenderer.needsRender = false; // it will get updated in the end of the method, we don't want weird "flashing" of the items that are supposed to be invisible

			await this._spaceViewRenderer.spaceItemController.updateFilterState(undefined, undefined, 0);

			this._spaceViewRenderer.needsRender = true;
		}
	}

	public getFilteredItems(): ISpaceItemModel[] {
		const {appState} = this._spaceViewRenderer.transport;
		const {actions} = appState;

		let items: ISpaceItemModel[] = [];

		if (this._spaceViewRenderer.space) {
			// #2697
			// Instead of the ItemManager.items, use the lists in the appState to fetch these items,
			// otherwise the cache values for filters will be wrong, because it's possible that the appState list
			// is already updated, but the "itemmanager.items" array is not updated yet (async)

			const boundaries: Boundary[] = actions
				.getList(XyiconFeature.Boundary)
				.filter((boundary: Boundary) => [...boundary.boundarySpaceMaps].some(this.isModelInSpace)) as Boundary[];

			const xyicons: Xyicon[] = actions.getList(XyiconFeature.Xyicon).filter(this.isModelInSpace) as Xyicon[];
			const markups: Markup[] = actions.getList(XyiconFeature.Markup).filter(this.isModelInSpace) as Markup[];

			items.push(...boundaries, ...xyicons);

			// Apply local filters
			items = filterModels(items, this.filterState, appState, XyiconFeature.SpaceEditor);

			items.push(...markups);
		}

		return items;
	}

	public updateFilterState(
		filterState?: IFilterState,
		updateCaptions: boolean = true,
		timeout: number = 100,
		addFullscreenLoaderText: boolean = true,
	) {
		if (addFullscreenLoaderText) {
			this._spaceViewRenderer.transport.appState.fullscreenLoaderText = "Updating layers, filters and captions...";
		}
		clearTimeout(this._updateFilterStateTimeoutId);

		this._updateFilterStatePromise = new Promise<void>((resolve, reject) => {
			this._updateFilterStatePromiseResolveSet.add(resolve);
		});

		// This function can be called from multiple places, even from the same action.
		// For example, if you create a xyicon inside a boundary, then it gets called from the "xyicon creation" logic
		// and also it gets called from the "linkcreated" signalR event, because a link is being created automatically
		// between the xyicon and the boundary.
		// As a workaround, we put it in a setTimeout, so it only gets called once per "action".
		this._updateFilterStateTimeoutId = window.setTimeout(async () => {
			let updateDetailsPanel = false;
			if (filterState) {
				this.filterState = filterState;
			}
			const filteredItems = new Set(this.getFilteredItems());
			const excludableItems = [...this._boundaryManager.items.array, ...this._xyiconManager.items.array];

			const captionedItemsWithVisibilityChange: CaptionedItem[] = [];

			for (const item of excludableItems) {
				const itemModel = item.spaceItemType === "boundary" ? (item.modelData as BoundarySpaceMap).parent : (item.modelData as ISpaceItemModel);

				const prevVisibility = item.isVisible;
				const wasSelected = item.isSelected;
				item.setVisibility(!item.layerSettings.isHidden && filteredItems.has(itemModel));

				if (wasSelected && !item.isVisible) {
					// "setVisibility" also deselects it if turned to false, so we don't need to deselect again
					// item.deselect();
					updateDetailsPanel = true;
				}

				if (item.isVisible !== prevVisibility && [XyiconFeature.Xyicon, XyiconFeature.Boundary].includes(item.modelData.ownFeature)) {
					captionedItemsWithVisibilityChange.push(item as CaptionedItem);
				}

				const layerSettings = item.layerSettings;
				if (layerSettings) {
					item.setPositionLockedInView(layerSettings.isPositionLocked);
				}
			}

			let shouldUpdateMarkupTexts: boolean = false;

			for (const item of this._markupManager.items.array) {
				const wasSelected = item.isSelected;
				const hasVisibilityChanged = (item as Markup3D).onLayerSettingsModified();
				if (hasVisibilityChanged && (item as Markup3D).textContent) {
					shouldUpdateMarkupTexts = true;
				}
				if (wasSelected && !item.isVisible) {
					updateDetailsPanel = true;
				}
			}

			if (shouldUpdateMarkupTexts) {
				this.markupTextManager.recreateGeometry();
			}

			if (updateCaptions) {
				// Update the captions only for the affected (newly added, or newly removed, or visibility changed) items

				if (captionedItemsWithVisibilityChange.length > 0) {
					const areThereBoundaries = captionedItemsWithVisibilityChange.some((item) => item.spaceItemType === "boundary");

					await this._spaceViewRenderer.actions.updateSpaceEditorCaptionsForAffectedAndLinkedItems(
						areThereBoundaries ? XyiconFeature.Boundary : XyiconFeature.Xyicon,
						captionedItemsWithVisibilityChange,
					);
				}
			}

			this.updateActionBar(false, updateDetailsPanel);
			this.syncLegacyRotationIcons();

			this.signals.filterUpdated.dispatch();
			this._spaceViewRenderer.needsRender = true;

			for (const resolve of [...this._updateFilterStatePromiseResolveSet]) {
				resolve();
				this._updateFilterStatePromiseResolveSet.delete(resolve);
			}
			this._spaceViewRenderer.transport.appState.fullscreenLoaderText = "";
		}, timeout);

		return this._updateFilterStatePromise;
	}

	/**
	 * If there are items that got added recently, and they are hidden by the current filter, show a notification to the user
	 * Note that it also calls "updateFilterState"
	 * @param items
	 */
	public async showNotificationForAddedHiddenItems(items: SpaceItem[]) {
		// .isEmbedded can be false (if there's a term for false negative, we should call this false false, lol). When the user copy-pastes a xyicon with embedded xyicons,
		// and the response from the server for the creation of the embedded ones arrive (so the modelData is overwritten),
		// but the new links haven't arrived yet through signalR. So we must check this condition before the request is sent to the server

		const newNonembeddedItems = items.filter((item) => !(item as Xyicon3D).parentXyicon);
		await this._spaceViewRenderer.spaceItemController.updateFilterState(undefined, undefined, undefined, false);

		// #2731: A delay to the notification to prevent false alarm (when signalr with the link updates arrive a little bit later than the backend response for the API call)
		await TimeUtils.wait(500);
		const showNotification = newNonembeddedItems.some((item: SpaceItem) => !item.isVisible);
		if (showNotification) {
			notifyAboutViewFilteringOutItems(
				items.map((item) => item.modelData as ISpaceItemModel),
				this._spaceViewRenderer,
			);
		}
	}

	public get isAddingItemsToServer() {
		const {xyiconManager, boundaryManager, markupManager} = this;

		return xyiconManager.isAddingItemsToServer || boundaryManager.isAddingItemsToServer || markupManager.isAddingItemsToServer;
	}

	public get isInEditMode() {
		return this._boundaryManager.isInEditMode || this._markupManager.isInEditMode;
	}

	public get currentlyEditedItem() {
		if (this._boundaryManager.isInEditMode) {
			return this._boundaryManager.currentlyEditedItem;
		} else if (this._markupManager.isInEditMode) {
			return this._markupManager.currentlyEditedItem;
		} else {
			return null;
		}
	}

	public get xyiconManager() {
		return this._xyiconManager;
	}

	public get boundaryManager() {
		return this._boundaryManager;
	}

	public get markupManager() {
		return this._markupManager;
	}

	public get managers() {
		return [this._xyiconManager, this._boundaryManager, this._markupManager];
	}

	public get rotationIconManager() {
		return this._rotationIconManager;
	}

	public get selectionHandleManager() {
		return this._selectionHandleManager;
	}

	public get linkIconManager() {
		return this._linkIconManager;
	}

	public get linkLineManager() {
		return this._linkLineManager;
	}

	public get lockIconManager() {
		return this._lockIconManager;
	}

	public get snapToGridManager() {
		return this._snapToGridManager;
	}

	public get boundingBox() {
		return this._boundingBox;
	}

	public get markupTextManager() {
		return this._markupTextManager;
	}

	public get captionCollisionSolver() {
		return this._captionCollisionSolver;
	}

	public get selectedItems() {
		return [
			...this._xyiconManager.selectedItems,
			...this._boundaryManager.selectedItems,
			...this._markupManager.selectedItems,
			...this._linkLineManager.selectedItems,
		];
	}
}
